import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

interface PromotionalStatus {
  has_active_promo: boolean;
  promo_code?: string;
  description?: string;
  tier_name?: string;
  original_price?: number;
  discounted_price?: number;
  savings_amount?: number;
  is_lifetime?: boolean;
  expires_at?: string | null;
  benefits?: any;
}

interface EarlyAdopterBadgeProps {
  variant?: 'compact' | 'detailed' | 'tooltip';
  className?: string;
}

const EarlyAdopterBadge: React.FC<EarlyAdopterBadgeProps> = ({ 
  variant = 'compact', 
  className = '' 
}) => {
  const [promotionalStatus, setPromotionalStatus] = useState<PromotionalStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    fetchPromotionalStatus();
  }, []);

  const fetchPromotionalStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: promoData, error } = await supabase
        .rpc('get_trainer_promotional_status', { p_trainer_id: user.id });

      if (!error && promoData) {
        setPromotionalStatus(promoData);
      }
    } catch (error) {
      console.error('Error fetching promotional status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null;
  }

  if (!promotionalStatus?.has_active_promo) {
    return null;
  }

  const getBadgeConfig = (tierName: string) => {
    const configs = {
      'Founders Circle': {
        color: 'bg-purple-100 text-purple-800 border-purple-200',
        icon: '👑',
        gradient: 'from-purple-50 to-purple-100',
        description: 'Exclusive lifetime discount'
      },
      'Early Access': {
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: '🚀',
        gradient: 'from-blue-50 to-blue-100',
        description: '6-month early adopter discount'
      },
      'Launch Special': {
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: '🎉',
        gradient: 'from-green-50 to-green-100',
        description: '3-month launch discount'
      }
    };

    return configs[tierName as keyof typeof configs] || {
      color: 'bg-gray-100 text-gray-800 border-gray-200',
      icon: '🎯',
      gradient: 'from-gray-50 to-gray-100',
      description: 'Special discount'
    };
  };

  const config = getBadgeConfig(promotionalStatus.tier_name || '');

  if (variant === 'compact') {
    return (
      <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${config.color} ${className}`}>
        <span className="mr-1">{config.icon}</span>
        {promotionalStatus.tier_name}
      </div>
    );
  }

  if (variant === 'tooltip') {
    return (
      <div className="relative inline-block">
        <div
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border cursor-help ${config.color} ${className}`}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <span className="mr-1">{config.icon}</span>
          {promotionalStatus.tier_name}
        </div>
        
        {showTooltip && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg shadow-lg z-50 whitespace-nowrap">
            <div className="text-center">
              <p className="font-medium">{promotionalStatus.tier_name}</p>
              <p className="text-xs opacity-90">{config.description}</p>
              {promotionalStatus.is_lifetime ? (
                <p className="text-xs text-green-300">Lifetime discount</p>
              ) : promotionalStatus.expires_at && (
                <p className="text-xs text-yellow-300">
                  Expires {new Date(promotionalStatus.expires_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        )}
      </div>
    );
  }

  // Detailed variant
  return (
    <div className={`bg-gradient-to-r ${config.gradient} rounded-lg border ${config.color.replace('text-', 'border-')} p-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <span className="text-2xl mr-3">{config.icon}</span>
          <div>
            <h3 className="font-semibold text-lg">{promotionalStatus.tier_name}</h3>
            <p className="text-sm opacity-80">{config.description}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm opacity-80">Save</p>
          <p className="font-bold text-lg">${promotionalStatus.savings_amount}/month</p>
        </div>
      </div>
      
      <div className="mt-3 pt-3 border-t border-current border-opacity-20">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="opacity-80">Original Price</p>
            <p className="line-through">${promotionalStatus.original_price}/month</p>
          </div>
          <div>
            <p className="opacity-80">Your Price</p>
            <p className="font-semibold">${promotionalStatus.discounted_price}/month</p>
          </div>
        </div>
        
        {promotionalStatus.is_lifetime ? (
          <p className="text-sm text-center mt-2 font-medium">✨ Lifetime discount applied</p>
        ) : promotionalStatus.expires_at && (
          <p className="text-sm text-center mt-2 opacity-80">
            Valid until {new Date(promotionalStatus.expires_at).toLocaleDateString()}
          </p>
        )}
      </div>
    </div>
  );
};

export default EarlyAdopterBadge;
